import { useState } from 'react';

export default function FAQ() {
  const [activeCategory, setActiveCategory] = useState('Services');
  const [expandedItem, setExpandedItem] = useState<number | null>(0);

  const categories = ['Services', 'Pricing', 'Delivery', 'Care Instructions', 'Policies'];

  const faqData: { [key: string]: { question: string; answer: string }[] } = {
    Services: [
      {
        question: 'What types of garments do you clean?',
        answer: 'We clean all types of garments including everyday clothing, formal wear, delicate fabrics, suits, dresses, coats, and household items like curtains and bedding. Our expert team handles each item with specialized care based on fabric type and manufacturer instructions.'
      },
      {
        question: 'Do you offer same-day service?',
        answer: 'Yes! Same-day service is available for our Premium and Executive package customers. For Basic Care customers, same-day service can be arranged for an additional fee. Please drop off items before 9 AM for same-day pickup.'
      },
      {
        question: 'How do you handle stains?',
        answer: 'We use advanced stain removal techniques and eco-friendly solutions tailored to each fabric type. Our specialists inspect every garment and apply appropriate pre-treatment before cleaning. Most common stains can be successfully removed, though results depend on stain age and fabric type.'
      },
      {
        question: 'Are your cleaning methods eco-friendly?',
        answer: 'Absolutely! We use environmentally safe cleaning solutions that are biodegradable and non-toxic. Our modern equipment is energy-efficient, and we recycle hangers and packaging materials. We are committed to protecting both your garments and the environment.'
      }
    ],
    Pricing: [
      {
        question: 'How is pricing calculated?',
        answer: 'We offer both monthly subscription packages and pay-per-item pricing. Subscriptions provide the best value for regular customers. Individual items are priced based on garment type and service required. Contact us for a detailed price list or custom quote.'
      },
      {
        question: 'Are there any hidden fees?',
        answer: 'No hidden fees ever! Our pricing is completely transparent. The only additional charges would be for special services you request, such as rush service, special stain treatment for difficult stains, or repairs, and these are always discussed with you first.'
      },
      {
        question: 'Do you offer discounts?',
        answer: 'Yes! We offer a 10% discount for first-time customers, 15% off for monthly subscription sign-ups, and special rates for bulk orders. We also have seasonal promotions and loyalty rewards for our regular customers.'
      }
    ],
    Delivery: [
      {
        question: 'Is pickup and delivery really free?',
        answer: 'Yes! Free pickup and delivery is included with all our subscription packages within our service area. For pay-per-item customers, free delivery is available for orders over $30. We offer flexible scheduling to fit your busy lifestyle.'
      },
      {
        question: 'What are your delivery hours?',
        answer: 'We offer pickup and delivery Monday through Saturday, 7 AM to 7 PM. Sunday service is available for Executive package customers. You can schedule your preferred time slot through our website, app, or by phone.'
      },
      {
        question: 'How do I schedule a pickup?',
        answer: 'Scheduling is easy! Use our online booking system, mobile app, or call us directly. You can set up recurring pickups or schedule one-time service. We will send you a confirmation and reminder before each pickup.'
      }
    ],
    'Care Instructions': [
      {
        question: 'How should I prepare items for pickup?',
        answer: 'Simply place your items in a bag or hamper. Check pockets for personal items and point out any specific stains or concerns. No need to sort by color or fabric type - our team handles all sorting and inspection.'
      },
      {
        question: 'What if my garment has special care requirements?',
        answer: 'Please let us know about any special requirements when dropping off or scheduling pickup. We follow all manufacturer care labels and can accommodate special requests. Our team is trained to identify and properly care for delicate and specialty fabrics.'
      },
      {
        question: 'How long do you keep unclaimed items?',
        answer: 'We hold completed orders for 30 days at no charge. After 30 days, a small storage fee applies. Items unclaimed after 90 days may be donated to charity. We will send multiple reminders before this occurs.'
      }
    ],
    Policies: [
      {
        question: 'What is your satisfaction guarantee?',
        answer: 'We stand behind our work with a 100% satisfaction guarantee. If you are not completely satisfied with our service, we will re-clean the item at no charge or provide a full refund. Your satisfaction is our top priority.'
      },
      {
        question: 'Are my items insured?',
        answer: 'Yes, all items in our care are insured. While we take every precaution to prevent loss or damage, we maintain comprehensive insurance coverage. In the rare event of an issue, we will work with you to reach a fair resolution.'
      },
      {
        question: 'Can I cancel my subscription anytime?',
        answer: 'Yes, our subscriptions are flexible with no long-term contracts. You can cancel, pause, or change your plan at any time. Simply give us 7 days notice before your next billing cycle.'
      }
    ]
  };

  return (
    <section id="faq" className="py-24 bg-stone-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-amber-600 uppercase tracking-wide mb-3">
            FREQUENTLY ASKED
          </p>
          <h2 className="text-5xl font-bold text-gray-900">Questions &amp; Answers</h2>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="lg:sticky lg:top-24">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Categories</h3>
              <ul className="space-y-2">
                {categories.map((category) => (
                  <li key={category}>
                    <button
                      onClick={() => {
                        setActiveCategory(category);
                        setExpandedItem(0);
                      }}
                      className={`w-full text-left px-4 py-3 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                        activeCategory === category
                          ? 'bg-amber-600 text-white'
                          : 'text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      <span className="flex items-center gap-3">
                        <span
                          className={`w-2 h-2 rounded-full ${
                            activeCategory === category ? 'bg-white' : 'bg-amber-600'
                          }`}
                        ></span>
                        {category}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="lg:col-span-8">
            <div className="space-y-4">
              {faqData[activeCategory]?.map((item, index) => (
                <div
                  key={index}
                  className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100"
                >
                  <button
                    onClick={() => setExpandedItem(expandedItem === index ? null : index)}
                    className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer hover:bg-gray-50 transition-colors"
                  >
                    <span className="text-lg font-semibold text-gray-900 pr-4">
                      {item.question}
                    </span>
                    <i
                      className={`text-2xl text-amber-600 transition-transform duration-300 flex-shrink-0 ${
                        expandedItem === index
                          ? 'ri-subtract-line'
                          : 'ri-add-line'
                      }`}
                    ></i>
                  </button>
                  <div
                    className={`transition-all duration-300 overflow-hidden ${
                      expandedItem === index ? 'max-h-96' : 'max-h-0'
                    }`}
                  >
                    <div className="px-6 pb-5">
                      <div className="h-px bg-gray-200 mb-4"></div>
                      <p className="text-base text-gray-600 leading-relaxed">{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
